import React, { useEffect, useMemo, useState } from 'react';
import './Bonusprint.css';
import axios from 'axios';
import { Document, Page, Text, View, StyleSheet, PDFViewer, Image } from '@react-pdf/renderer';
import { Font } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import LatoItalicFont from "../../Fonts/Baloo2-VariableFont_wght.ttf";
import NotoSansKannada from "../../Fonts/NotoSansKannada.ttf";
import Logo from "../../imgs/dairylogo.png";

Font.register({
    family: 'Lato-Italic',
    src: LatoItalicFont,
});

Font.register({
    family: 'Kannada',
    src: NotoSansKannada,
});

// ---------- helpers ----------
const toNum = (v) => (v == null || v === '' || isNaN(v) ? 0 : Number(v));
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const chunk = (arr, size) =>
    Array.from({ length: Math.ceil((arr?.length || 0) / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
    );

// Pull config from `input` if provided, otherwise from localStorage
const getCalcConfig = (input) => {
    const type = String(input?.type ?? localStorage.getItem('type') ?? '').toLowerCase();
    const isPer = type === 'per';
    const mhRate = toNum(input?.mhrate ?? localStorage.getItem('mhrate'));
    const gyRate = toNum(input?.gayrate ?? localStorage.getItem('gayrate'));
    return { isPer, mhRate, gyRate };
};

// Sum totals; set roundEachRow=true to match visible per-row 2-dec values
const computeTotals = (rows, cfg, roundEachRow = false) => (
    (Array.isArray(rows) ? rows : []).reduce((acc, r) => {
        const thev = toNum(r.thevamt);

        const mhliter = toNum(r.mhliter);
        const gyliter = toNum(r.gayliter);

        const mh = cfg.isPer ? toNum(r.mhamt) : toNum(r.mhliter);
        const gy = cfg.isPer ? toNum(r.gayamt) : toNum(r.gayliter);

        const mhamt = toNum(r.mhamt);
        const gyamt = toNum(r.gayamt);

        let mhBonus = cfg.isPer ? (mh * cfg.mhRate) / 100 : (mh * cfg.mhRate);
        let gyBonus = cfg.isPer ? (gy * cfg.gyRate) / 100 : (gy * cfg.gyRate);

        if (roundEachRow) {
            mhBonus = round2(mhBonus);
            gyBonus = round2(gyBonus);
        }

        acc.thevamt += thev;
        acc.mhliter += mhliter;
        acc.mhamt += mhamt;
        acc.gayliter += gyliter;
        acc.gayamt += gyamt;
        acc.mhBonus += mhBonus;
        acc.gyBonus += gyBonus;
        return acc;
    }, { thevamt: 0, mhliter: 0, gayliter: 0, mhBonus: 0, gyBonus: 0, mhamt: 0, gayamt: 0 })
);

const createStyles = (fontBase) => StyleSheet.create({
    page: {
        paddingTop: 10,
        flexDirection: 'row',
        justifyContent: 'center',
    },
    header: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        textAlign: 'center',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100vw',
        justifyContent: 'space-between',
    },
    table: {
        marginTop: 4,
        display: 'table',
        width: '97%',
        margin: 5,
    },
    tableone: {
        display: 'table',
        width: '18%',
        margin: 5,
    },
    tableCellone: {
        border: '0.5px solid lightgray',
        padding: 0.5,
        fontSize: 8,
        fontFamily: fontBase,
        textAlign: 'center',
        flex: 1,
    },
    tableRowone: {
        flexDirection: 'row',
        width: '100%',
    },
    tableHeaderone: {
        fontFamily: fontBase,
        fontWeight: 'bold',
        border: '1px solid #000',
        padding: 2,
        fontSize: 8,
        textAlign: 'center',
        flex: 1,
    },

    tableRow: {
        flexDirection: 'row',
    },
    tableRow2: {
        flexDirection: 'row',
        backgroundColor: 'lightgray',
        marginTop: 5,
    },

    tableRow3: {
        flexDirection: 'row',
        backgroundColor: 'lightgray',
        marginTop: 10,
    },
    line: {
        flexDirection: 'row',
        backgroundColor: 'lightgray',
        marginTop: 5,
        height: 1,
        width: '100vw',
        marginBottom: 5,
    },
    tableHeader: {
        fontFamily: fontBase,
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderLeftWidth: 1,
        borderRightWidth: 0,
        borderStyle: 'solid',
        padding: 2,
        flex: 0.5,
        borderColor: 'lightblack',
        textAlign: 'center',
        fontSize: 10,
    },
    tableHeaderName: {
        fontFamily: fontBase,
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderLeftWidth: 1,
        borderRightWidth: 0,
        borderStyle: 'solid',
        padding: 2,
        flex: 1,
        borderColor: 'lightblack',
        textAlign: 'center',
        fontSize: 10,
    },
    tableHeaderkno: {
        fontFamily: fontBase,
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderLeftWidth: 1,
        borderRightWidth: 0,
        borderStyle: 'solid',
        padding: 2,
        flex: 0.25,
        borderColor: 'lightblack',
        textAlign: 'center',
        fontSize: 10,
        width: 2.5
    },

    tableHeaderlast: {
        fontFamily: fontBase,
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderStyle: 'solid',
        borderColor: 'lightblack',
        padding: 2,
        flex: 0.5,
        textAlign: 'center',
        fontSize: 9,
    },
    tableCell: {
        border: '0.5px solid black',
        padding: 0.5,
        flex: 1,
        fontSize: 10,
        fontFamily: fontBase,
        width: 2,
        textAlign: 'center',
    },
    tableCellName: {
        border: '0.5px solid black',
        padding: 0.5,
        flex: 2,
        fontSize: 10,
        fontFamily: fontBase,
        width: 4,
        textAlign: 'center',
    },
    tableCellkno: {
        border: '0.5px solid black',
        padding: 0.5,
        flex: 0.5,
        fontSize: 10,
        fontFamily: fontBase,
        width: 1,
        textAlign: 'center',
        width: 5
    },
    tableCellgreen: {
        border: '0.5px solid lightgray',
        padding: 0.5,
        flex: 1,
        fontSize: 10,
        fontWeight: 'bold',
        fontFamily: fontBase,
        color: 'green',
        width: 2,
        textAlign: 'center',
    },
    tableCellred: {
        border: '0.5px solid lightgray',
        padding: 0.5,
        flex: 1,
        fontSize: 9,
        fontWeight: 'bold',
        fontFamily: fontBase,
        color: 'red',
        width: 2,
        textAlign: 'center',
    },
    latoTextlarge: {
        fontFamily: fontBase,
        fontSize: 15,
        marginBottom: 5,
        textAlign: 'center',
        width: '100vw',
        fontWeight: 'bold',
        marginTop: 5,
    },
    latoTexthead: {
        fontFamily: fontBase,
        fontSize: 12,
        textAlign: 'center',
        width: '100vw',
        fontWeight: 'bold',

    },
    latoText: {
        fontFamily: fontBase,
        fontSize: 12,
        textAlign: 'center',
        width: '100vw',
        fontWeight: 'bold',
        marginTop: 5,
    },
    latoTextd2: {
        fontFamily: fontBase,
        fontSize: 12,
        textAlign: 'center',
        width: '90vw',
        fontWeight: 'bold',

    },
    latoText2: {
        fontFamily: fontBase,
        fontSize: 11,
        textAlign: 'center',
        width: '100vw',
        fontWeight: 'bold',
    },
    headtextsmall: {
        fontFamily: fontBase,
        fontSize: 10,
        fontWeight: 'bold',
    },
    headtextsmall2: {
        fontFamily: fontBase,
        fontSize: 9,
        fontWeight: 'bold',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        width: '100vw',
    },
    row1: {
        display: 'flex',
        flexDirection: 'row',
        width: '100vw',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 5,
        paddingRight: 5,
    },
    row2: {
        display: 'flex',
        flexDirection: 'row',
        width: '100vw',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 140,
        paddingRight: 140,
        backgroundColor: 'lightgray',
    },
    column1: {
        display: 'flex',
        flexDirection: 'column',
        width: '19%',
        justifyContent: 'start',
        alignItems: 'flex-start',
        paddingLeft: 5,
        paddingRight: 5,
        marginLeft: 20,
    },
    column2: {
        display: 'flex',
        flexDirection: 'column',
        width: '15%',
        justifyContent: 'start',
        alignItems: 'flex-start',
        paddingLeft: 5,
        paddingRight: 5,
    },
    column3: {
        display: 'flex',
        flexDirection: 'column',
        width: '17%',
        justifyContent: 'start',
        alignItems: 'flex-start',
        paddingLeft: 5,
        paddingRight: 5,
        marginLeft: 20,
    },
    column1Row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    column1Rowgray: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: 6,
        marginTop: 5,
        border: '0.5px solid black',
        backgroundColor: 'lightgray'
    },
    column1Label: {
        fontFamily: fontBase,
        fontSize: 9,
        fontWeight: 'bold',
        textAlign: 'left',
    },
    column1Value: {
        fontFamily: fontBase,
        fontSize: 9,
        fontWeight: 'bold',
        textAlign: 'right',
    },
    pageBreak: {
        pageBreakBefore: 'always',
    },
    totalsRow: {
        flexDirection: 'row',
        borderTop: '1px solid #000',
        paddingTop: 5,
        paddingLeft: 2,
    },
    totalsCell: {
        border: '0.5px solid lightgray',
        padding: 0.5,
        flex: 1,
        fontSize: 8,
        width: 2,
        textAlign: 'center',
        fontFamily: fontBase,
    },
    imagerow: {
        display: 'flex',
        flexDirection: 'row',
        width: '100vw',
        justifyContent: 'center',
        alignItems: 'center',
    },
    image: {
        width: 80,
        height: "auto",
        marginLeft: -15,
        marginTop: 5

    },
    imgtext: {
        fontFamily: fontBase,
        fontSize: 9,
        fontWeight: 'bold',
        textAlign: 'center',
    },

    pagePadded: {
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 12,
        paddingBottom: 12,
    },

    colBase: {
        flex: 1,
        borderStyle: 'solid',
        borderWidth: 1.5,
        padding: 4,
        fontSize: 10,
        fontFamily: fontBase,
    },

    card: { marginBottom: 10, paddingBottom: 8 },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    infoLeft: { fontFamily: fontBase, fontSize: 10 },
    infoRight: { fontFamily: fontBase, fontSize: 10, textAlign: 'right' },

    tableCelllast: {
        fontSize: 10, padding: 4, borderRightWidth: 1, borderBottomWidth: 1, textAlign: 'right', fontFamily: fontBase
    },
    greeting: { fontFamily: fontBase, fontSize: 10, marginTop: 6, fontWeight: 'bold' },

    sectionDivider: {
        marginTop: 6,
        borderStyle: 'dotted',
        borderTopWidth: 1,
        borderColor: '#bbb',
        height: 0,
        width: '100%',
    },
    colHeader: {
        fontWeight: 'bold',
        textAlign: 'center',
        flex: 1,
        borderStyle: 'solid',
        border: '0.7px solid black',
        padding: 4,
        fontSize: 10,
        fontFamily: fontBase,
        color: 'black'
    },
    colHeaderLast: {
    },

    colNum: {
        textAlign: 'right',
        flex: 1,
        border: '0.7px solid black',
        borderStyle: 'solid',
        padding: 4,
        fontSize: 10,
        fontFamily: fontBase,
    },

});

// ---------- component ----------
const Bonusprint = () => {
    const { t, i18n } = useTranslation();
    const [customerData, setCustomerData] = useState([]);

    const lang = i18n?.language || localStorage.getItem("i18nextLng") || "mr";
    const fontBase = (lang || "").toLowerCase().startsWith("kn") ? "Kannada" : "Lato-Italic";
    const styles = useMemo(() => createStyles(fontBase), [fontBase]);

    useEffect(() => {
        const jsEncode = {
            encode: function (s, k) {
                let enc = "";
                const str = s.toString();
                for (let i = 0; i < str.length; i++) {
                    const a = str.charCodeAt(i);
                    const b = a ^ k;
                    enc = enc + String.fromCharCode(b);
                }
                return enc;
            }
        };

        const mainuser2 = jsEncode.encode("mamapr", 125);
        let rawData = {
            date1: localStorage.getItem("date1print"),
            date2: localStorage.getItem("date2print"),
            knofrom: localStorage.getItem("knofrom"),
            knoto: localStorage.getItem("knoto"),
            mainuser: localStorage.getItem("contact"),
            type: localStorage.getItem("type"),
            showthevcol: localStorage.getItem("showthevcol"),
            mobileuser: localStorage.getItem("mobileuser"),
            mainuser2: mainuser2,
        };
        rawData = JSON.stringify(rawData);
        const formData = new FormData();
        formData.append('data', rawData);

        axios.post(`https://excellentdairy.com/dairy/php/cust/getbonusreport4.php`, formData, {
            'Content-Type': 'multipart/form-data',
        })
            .then(res => setCustomerData(res.data || []))
            .catch(console.error);
    }, []);

    // config used by rows + totals
    const { isPer, mhRate, gyRate } = getCalcConfig();

    function formatDate(inputDate) {
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(inputDate).toLocaleDateString('en-US', options);
    }

    // prepare pages of 5 rows each
    const printDesign = (localStorage.getItem("printdesign") || "1");
    const perPage = printDesign === "2" ? 5 : 12;
    const pages = chunk(customerData, perPage);

    const grandTotals = computeTotals(customerData, { isPer, mhRate, gyRate }, true);
    const grandTTBonus = grandTotals.mhBonus + grandTotals.gyBonus + grandTotals.thevamt;

    const colLabelMh = localStorage.getItem("type") === "liter"
        ? t("bonusprint.labels.buffaloLiter")
        : localStorage.getItem("type") === "per"
            ? t("bonusprint.labels.buffaloAmount")
            : t("bonusprint.labels.buffaloLiter");

    const colLabelGy = localStorage.getItem("type") === "liter"
        ? t("bonusprint.labels.cowLiter")
        : localStorage.getItem("type") === "per"
            ? t("bonusprint.labels.cowAmount")
            : t("bonusprint.labels.cowLiter");

    return (
        <PDFViewer className='pdf'>
            <Document>
                {pages.length > 0 && pages.map((rows, pageIdx) => (
                    localStorage.getItem("printdesign") === "1" ?
                        <Page key={pageIdx} orientation='landscape' size="A4">
                            {/* header */}
                            <Text style={styles.latoText}>{localStorage.getItem('kendra') || ""}</Text>
                            <Text style={styles.latoTexthead}>{t("bonusprint.header.title")}</Text>
                            <Text style={styles.latoText2}>
                                {t("bonusprint.header.period", {
                                    from: formatDate(localStorage.getItem("date1print")),
                                    to: formatDate(localStorage.getItem("date2print"))
                                })}
                            </Text>

                            {/* table */}
                            <View style={styles.table}>
                                {/* header row */}
                                <View style={styles.tableRow}>
                                    <Text style={styles.tableHeaderkno}>{t("bonusprint.table.accountNo")}</Text>
                                    <Text style={styles.tableHeaderName}>{t("bonusprint.table.name")}</Text>
                                    {
                                        localStorage.getItem("showthevcol") === "Yes" ?
                                            <Text style={styles.tableHeader}>{t("bonusprint.table.depositAmount")}</Text>
                                            : ''
                                    }
                                    <Text style={styles.tableHeaderName}>{t("bonusprint.table.bankAccount")}</Text>
                                    <Text style={styles.tableHeader}>{colLabelMh}</Text>
                                    <Text style={styles.tableHeader}>{t("bonusprint.table.buffaloAmount")}</Text>
                                    <Text style={styles.tableHeader}>{t("bonusprint.table.buffaloBonus")}</Text>
                                    <Text style={styles.tableHeader}>{colLabelGy}</Text>
                                    <Text style={styles.tableHeader}>{t("bonusprint.table.cowAmount")}</Text>
                                    <Text style={styles.tableHeader}>{t("bonusprint.table.cowBonus")}</Text>
                                    <Text style={styles.tableHeader}>{t("bonusprint.table.totalBonus")}</Text>
                                    {
                                        localStorage.getItem("showthevcol") === "Yes" ?
                                            <Text style={styles.tableHeaderlast}>{t("bonusprint.table.depositPlusBonus")}</Text>
                                            : ''}
                                    <Text style={styles.tableHeaderlast}>{t("bonusprint.table.signature")}</Text>
                                </View>

                                {/* data rows */}
                                {rows.map((rowData, index) => {
                                    const thevamt = toNum(rowData.thevamt);

                                    const mhliter = toNum(rowData.mhliter);
                                    const mh = isPer ? toNum(rowData.mhamt) : toNum(rowData.mhliter);
                                    const mhamt = toNum(rowData.mhamt);

                                    const gyliter = toNum(rowData.gayliter);
                                    const gy = isPer ? toNum(rowData.gayamt) : toNum(rowData.gayliter);
                                    const gyamt = toNum(rowData.gayamt);

                                    const mhBonus = isPer ? (mh * mhRate) / 100 : (mh * mhRate);
                                    const gyBonus = isPer ? (gy * gyRate) / 100 : (gy * gyRate);
                                    const totalBonus = mhBonus + gyBonus;

                                    return (
                                        <View key={index} style={styles.tableRow}>
                                            <Text style={styles.tableCellkno}>{rowData.kno}</Text>
                                            <Text style={styles.tableCellName}>{rowData.cname + "\n "}</Text>
                                            {
                                                localStorage.getItem("showthevcol") === "Yes" ?
                                                    <Text style={styles.tableCell}>{thevamt.toFixed(2)}</Text>
                                                    : ''
                                            }
                                            <Text style={styles.tableCellName}>{rowData.accno}</Text>
                                            <Text style={styles.tableCell}>{mhliter.toFixed(2)}</Text>
                                            <Text style={styles.tableCell}>{mhamt.toFixed(2)}</Text>
                                            <Text style={styles.tableCell}>{mhBonus.toFixed(2)}</Text>

                                            <Text style={styles.tableCell}>{gyliter.toFixed(2)}</Text>
                                            <Text style={styles.tableCell}>{gyamt.toFixed(2)}</Text>
                                            <Text style={styles.tableCell}>{gyBonus.toFixed(2)}</Text>
                                            <Text style={styles.tableCell}>{parseFloat(totalBonus).toFixed(2)}</Text>
                                            {
                                                localStorage.getItem("showthevcol") === "Yes" ?
                                                    <Text style={styles.tableCell}>{(totalBonus + thevamt).toFixed(2)}</Text>
                                                    : ''}
                                            <Text style={styles.tableCell}></Text>
                                        </View>
                                    );
                                })}

                                {/* grand totals only on the LAST page */}
                                {pageIdx === pages.length - 1 && (
                                    <View style={styles.tableRow3}>
                                        <Text style={styles.tableHeaderkno}>{t("bonusprint.table.total")}</Text>
                                        <Text style={styles.tableHeaderName}>{t("bonusprint.table.dash")}</Text>
                                        {
                                            localStorage.getItem("showthevcol") === "Yes" ?
                                                <Text style={styles.tableHeader}>{grandTotals.thevamt.toFixed(2)}</Text>
                                                : ''}
                                        <Text style={styles.tableHeaderName}>{t("bonusprint.table.dash")}</Text>
                                        <Text style={styles.tableHeader}>{grandTotals.mhliter.toFixed(2)}</Text>
                                        <Text style={styles.tableHeader}>{grandTotals.mhamt.toFixed(2)}</Text>
                                        <Text style={styles.tableHeader}>{grandTotals.mhBonus.toFixed(2)}</Text>
                                        <Text style={styles.tableHeader}>{grandTotals.gayliter.toFixed(2)}</Text>
                                        <Text style={styles.tableHeader}>{grandTotals.gayamt.toFixed(2)}</Text>
                                        <Text style={styles.tableHeader}>{grandTotals.gyBonus.toFixed(2)}</Text>
                                        <Text style={styles.tableHeader}>{(grandTotals.mhBonus + grandTotals.gyBonus).toFixed(2)}</Text>
                                        {
                                            localStorage.getItem("showthevcol") === "Yes" ?
                                                <Text style={styles.tableHeaderlast}>{grandTTBonus.toFixed(2)}</Text>
                                                : ''}
                                        <Text style={styles.tableHeaderlast}>{t("bonusprint.table.dash")}</Text>
                                    </View>
                                )}
                            </View>
                        </Page>
                        :
                        <Page key={pageIdx} size="A4" style={styles.pagePadded}>
                            {rows.map((rowData, index) => {
                                const thevamt = toNum(rowData.thevamt);
                                const mh = toNum(rowData.mhliter);
                                const gy = toNum(rowData.gayliter);

                                const mhamt = toNum(rowData.mhamt);
                                const gyamt = toNum(rowData.gayamt);

                                const mhBonus = isPer ? (mhamt * mhRate) / 100 : (mh * mhRate);
                                const gyBonus = isPer ? (gyamt * gyRate) / 100 : (gy * gyRate);
                                const totalBonus = mhBonus + gyBonus;

                                return (
                                    <View key={index} style={styles.card}>
                                        {/* Center title above each record */}
                                        <Text style={styles.latoTextd2}>{localStorage.getItem('kendra') || ""}</Text>

                                        <Text style={styles.headtextsmall2}>
                                            {t("bonusprint.header.rateLine", { mhRate, gyRate })}
                                        </Text>

                                        {/* left | right */}
                                        <View style={styles.infoRow}>
                                            <Text style={styles.infoLeft}>
                                                {t("bonusprint.header.memberName", {
                                                    kno: rowData.kno,
                                                    name: rowData.cname
                                                })}
                                            </Text>
                                            <Text style={styles.infoRight}>
                                                {t("bonusprint.header.period", {
                                                    from: formatDate(localStorage.getItem("date1print")),
                                                    to: formatDate(localStorage.getItem("date2print"))
                                                })}
                                            </Text>
                                        </View>

                                        {/* table */}
                                        <View style={styles.table}>
                                            <View style={styles.tableRow}>
                                                {
                                                    localStorage.getItem("showthevcol") === "Yes" ?
                                                        <Text style={styles.colHeader}>{t("bonusprint.table.depositAmount")}</Text>
                                                        : ''}
                                                <Text style={styles.colHeader}>{colLabelMh}</Text>
                                                <Text style={styles.colHeader}>{t("bonusprint.table.buffaloAmount")}</Text>
                                                <Text style={styles.colHeader}>{t("bonusprint.table.buffaloBonus")}</Text>
                                                <Text style={styles.colHeader}>{colLabelGy}</Text>
                                                <Text style={styles.colHeader}>{t("bonusprint.table.cowAmount")}</Text>
                                                <Text style={styles.colHeader}>{t("bonusprint.table.cowBonus")}</Text>
                                                <Text style={styles.colHeader}>{t("bonusprint.table.totalBonus")}</Text>
                                                {
                                                    localStorage.getItem("showthevcol") === "Yes" ?
                                                        <Text style={[styles.colHeader, styles.colHeaderLast]}>{t("bonusprint.table.depositPlusBonus")}</Text>
                                                        : ''}
                                            </View>

                                            <View style={styles.tableRow}>
                                                {
                                                    localStorage.getItem("showthevcol") === "Yes" ?
                                                        <Text style={styles.colNum}>{thevamt.toFixed(2)}</Text>
                                                        : ' '}
                                                <Text style={styles.colNum}>{mh.toFixed(2)}</Text>
                                                <Text style={styles.colNum}>{mhamt.toFixed(2)}</Text>
                                                <Text style={styles.colNum}>{mhBonus.toFixed(2)}</Text>
                                                <Text style={styles.colNum}>{gy.toFixed(2)}</Text>
                                                <Text style={styles.colNum}>{gyamt.toFixed(2)}</Text>
                                                <Text style={styles.colNum}>{gyBonus.toFixed(2)}</Text>
                                                <Text style={[styles.colNum, styles.colLast]}>
                                                    {(totalBonus).toFixed(2)}
                                                </Text>
                                                {
                                                    localStorage.getItem("showthevcol") === "Yes" ?
                                                        <Text style={[styles.colNum, styles.colLast]}>
                                                            {(totalBonus + thevamt).toFixed(2)}
                                                        </Text>
                                                        : ''}

                                            </View>
                                        </View>

                                        <Text style={styles.greeting}>
                                            {t("bonusprint.header.greeting")}
                                        </Text>

                                        {index !== rows.length - 1 && <View style={styles.sectionDivider} />}
                                    </View>
                                );
                            })}
                        </Page>
                ))}
            </Document>
        </PDFViewer>
    );
};

export default Bonusprint;